export type InterviewStatus = 'applied' | 'scheduled' | 'interviewing' | 'offer' | 'rejected' | 'withdrawn';
export type InterviewMode = 'remote' | 'onsite' | 'phone' | 'hybrid';

export type InterviewRecord = {
  id: number;
  company: string;
  role: string;
  status: InterviewStatus;
  stage: string;
  interviewAt: string;
  mode: InterviewMode;
  location: string;
  jobUrl: string;
  contactName: string;
  contactEmail: string;
  salaryRange: string;
  preparationFocus: string;
  questionsAsked: string;
  notes: string;
  nextAction: string;
  followUpAt: string;
  createdAt?: string;
  updatedAt?: string;
};

export type InterviewInput = Omit<InterviewRecord, 'id' | 'createdAt' | 'updatedAt'>;

const DEMO_KEY = 'iprep_demo_interviews';

function localDate(daysFromNow: number, hour = 10) {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  date.setHours(hour, 30, 0, 0);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

const demoSeed: InterviewRecord = {
  id: 1,
  company: 'Acme Cloud',
  role: 'Senior Software Engineer',
  status: 'scheduled',
  stage: 'System design round',
  interviewAt: localDate(3),
  mode: 'remote',
  location: 'Video meeting link in calendar',
  jobUrl: '',
  contactName: 'Maya · Technical Recruiter',
  contactEmail: 'maya@example.com',
  salaryRange: '₹35L – ₹45L',
  preparationFocus: 'Caching, database sharding, API design, and explaining tradeoffs clearly.',
  questionsAsked: '',
  notes: 'Ask about team ownership, on-call expectations, and the first 90 days.',
  nextAction: 'Complete one 45-minute system design mock and send availability confirmation.',
  followUpAt: localDate(1).slice(0, 10),
};

export const emptyInterview = (): InterviewInput => ({
  company: '', role: '', status: 'applied', stage: 'Application', interviewAt: '', mode: 'remote', location: '', jobUrl: '',
  contactName: '', contactEmail: '', salaryRange: '', preparationFocus: '', questionsAsked: '', notes: '', nextAction: '', followUpAt: '',
});

export function loadDemoInterviews(): InterviewRecord[] {
  try {
    const stored = localStorage.getItem(DEMO_KEY);
    return stored ? JSON.parse(stored) : [demoSeed];
  } catch {
    return [demoSeed];
  }
}

function saveDemo(records: InterviewRecord[]) {
  localStorage.setItem(DEMO_KEY, JSON.stringify(records));
  return records;
}

export function createDemoInterview(input: InterviewInput) {
  const records = loadDemoInterviews();
  const record: InterviewRecord = { ...input, id: Date.now(), createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
  return saveDemo([record, ...records]);
}

export function updateDemoInterview(id: number, input: InterviewInput) {
  return saveDemo(loadDemoInterviews().map((record) => record.id === id ? { ...record, ...input, updatedAt: new Date().toISOString() } : record));
}

export function deleteDemoInterview(id: number) {
  return saveDemo(loadDemoInterviews().filter((record) => record.id !== id));
}
